import {
  ImageUpload,
  ImageUploadRef,
} from '@/components/FileUpload/ImageUpload';
import Modal from '@/components/Modal';
import { TFunction } from 'i18next';
import { useRef, useState, useEffect, Dispatch, SetStateAction } from 'react';
import {
  useCreateManyFirestoreImages,
  useCreateManyProcessings,
  useUpdateProcessing,
  useUploadProcessingImage,
} from '@/hooks/ai.hooks';
import { HumanModerationStatus } from '@/api/entities/moderation';
import Loader from '@/components/Loading/Loader';
import { createFileFromUrl } from '@/utils/file.utils';
import { R2_PUBLIC_DOMAIN } from '@/utils/constant.utils';
import { mapComponentOptions } from '@/utils/ia.utils';
import { Select } from '@/components/Controls/Select';
import { twMerge } from 'tailwind-merge';
import { Processing } from '@/api/entities/processing';

const MODAL_ID = 'engine-correction-upload-modal';

enum SubmitType {
  ADD = 'ADD',
  EDIT = 'EDIT',
}

interface EngineCorrectionUploadModalProps {
  t?: TFunction<undefined>;
  keyCarsData: [];
  defectKeyCarsData: [];
  selectedProcessing?: Processing;
  setSelectedProcessing?: Dispatch<SetStateAction<Processing | undefined>>;
  onUploadingImages?: (state: boolean) => void;
}

export const EngineCorrectionUploadModal = ({
  t,
  keyCarsData,
  defectKeyCarsData,
  selectedProcessing,
  setSelectedProcessing,
  onUploadingImages,
}: EngineCorrectionUploadModalProps) => {
  const imageUploadModalRef = useRef<ImageUploadRef>(null);
  const [hasImages, setHasImages] = useState(false);

  const submitTypeRef = useRef<SubmitType>(SubmitType.ADD);

  const [selectedComponentKey, setSelectedComponentKey] = useState('');
  const [selectedDefectKey, setSelectedDefectKey] = useState('');

  // const categoryIsMissing =
  //   hasImages && !selectedComponentKey && !selectedDefectKey;

  useEffect(() => {
    submitTypeRef.current = selectedProcessing
      ? SubmitType.EDIT
      : SubmitType.ADD;
    setSelectedComponentKey(selectedProcessing?.key);
    setSelectedDefectKey(selectedProcessing?.defectKey);
    setHasImages(selectedProcessing ? true : false);
  }, [selectedProcessing]);

  const {
    mutate: createManyProcessings,
    isPending: isPendingCreateManyProcessings,
  } = useCreateManyProcessings({ t });

  const { mutate: updateProcessing, isPending: isPendingUpdateProcessing } =
    useUpdateProcessing({ t, showDefaultSuccessMessage: true });

  const {
    mutate: createManyFirestoreImages,
    isPending: isPendingCreateManyFirestoreImages,
  } = useCreateManyFirestoreImages({
    t,
    handleSuccess: () => {
      handleEmptyImageList();
    },
  });

  const { mutate: uploadImages, isPending: isPendingUploadImages } =
    useUploadProcessingImage({
      handleSuccess: (data: {
        data: { files: Array<{ filename: string; path: string }> };
      }) => {
        const uploadedProcessingImages = data.data.files.map((file) => {
          const filePath = `${R2_PUBLIC_DOMAIN}/${file.path}`;

          return {
            status: HumanModerationStatus.A_MODERER,
            publicUrl: filePath,
            fileName: filePath,
            numElementsDetected: 0,
            numDamagesDetected: 0,
            key: selectedComponentKey,
            defectKey: selectedDefectKey,
            isDamage: !!selectedDefectKey,
            isManualUpload: true,
          };
        });

        const uploadedFirestoreImages = data.data.files.map((file) => {
          const filePath = `${R2_PUBLIC_DOMAIN}/${file.path}`;

          return {
            imageUrl: filePath,
            key: selectedComponentKey,
            defectKey: selectedDefectKey,
            isDamage: !!selectedDefectKey,
          };
        });

        if (submitTypeRef.current === SubmitType.ADD) {
          createManyProcessings(uploadedProcessingImages);
          createManyFirestoreImages(uploadedFirestoreImages);
        } else {
          updateProcessing({
            id: selectedProcessing?.id,
            ...uploadedProcessingImages[0],
          });
        }

        onUploadingImages?.(false);
      },
    });

  function handleEmptyImageList() {
    imageUploadModalRef.current?.resetImages();
    setSelectedProcessing?.(undefined);
    setHasImages(false);
    setSelectedComponentKey('');
    setSelectedDefectKey('');
  }

  const handleConfirm = async () => {
    const imageUrls = imageUploadModalRef.current?.images || [];
    const files = await Promise.all(
      imageUrls.map((url) =>
        createFileFromUrl(url, `${crypto.randomUUID()}.jpg`)
      )
    );
    onUploadingImages?.(true);
    uploadImages(files);
  };

  return (
    <>
      <Modal
        id={MODAL_ID}
        title={
          selectedProcessing
            ? t('common:modifyThisImage')
            : t('common:uploadImages')
        }
        size="large"
        containerClassName="top-6"
        bodyClassName="px-6 pt-5 m-0"
        showSecondBtn
        secondBtnLabel={t('common:confirm')}
        // disabledSubmitButton={
        //   !hasImages || (!selectedComponentKey && !selectedDefectKey)
        // }
        disabledSubmitButton={!hasImages}
        onClickSecondBtn={handleConfirm}
        onClose={handleEmptyImageList}
        onClickCancelBtn={handleEmptyImageList}
        onClickOutsideContent={handleEmptyImageList}
        onEscapePress={handleEmptyImageList}
      >
        <div
          className={twMerge(
            'mb-5 border border-gray-E5E5E5 px-5 pt-4 pb-6 rounded-lg'
            // categoryIsMissing && 'border-red-FF2929/20'
          )}
        >
          <div className="font-medium text-primary mb-7">
            {/* <span className={twMerge(categoryIsMissing && 'text-red-FF2929')}> */}
            <span>
              {selectedProcessing
                ? t('chooseEquipmentAndOrDamage')
                : t('chooseEquipmentAndOrDamage_plural')}
            </span>
            {/* <span className="ml-1.5 text-red-FF2929 text-lg">*</span> */}
            <span className="ml-1.5 font-semibold">:</span>
          </div>
          <div className="flex items-center">
            <div className="flex items-center gap-2">
              <div className="text-sm text-gray-700 font-medium">{`${t(
                'common:chooseAnEquipment'
              )} :`}</div>
              <Select
                selectClassName="h-[36px] w-max max-w-[280px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                options={mapComponentOptions(keyCarsData, '1', t)}
                onChange={(e) => setSelectedComponentKey(e.target.value)}
                value={selectedComponentKey}
              />
            </div>
            <div className="text-gray-300 ml-16 mr-12">|</div>
            <div className="flex items-center gap-2">
              <div className="text-sm text-gray-700 font-medium">{`${t(
                'common:chooseADamage'
              )} :`}</div>
              <Select
                selectClassName="h-[36px] w-max max-w-[280px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                options={mapComponentOptions(defectKeyCarsData, '2', t)}
                onChange={(e) => setSelectedDefectKey(e.target.value)}
                value={selectedDefectKey}
              />
            </div>
          </div>
        </div>
        <ImageUpload
          ref={imageUploadModalRef}
          t={t}
          allowedFormats={['jpg', 'jpeg', 'png']}
          multiple={selectedProcessing ? false : true}
          maxImageSize={5}
          onImagesChange={setHasImages}
          disabledChooseAnotherImgButton={isPendingUploadImages}
          initialImage={selectedProcessing?.imageUrl}
        />
      </Modal>
      {(isPendingUploadImages ||
        isPendingCreateManyProcessings ||
        isPendingCreateManyFirestoreImages ||
        isPendingUpdateProcessing) && <Loader />}
    </>
  );
};
